import type {
  PhysicalCardLifecycleControls,
  PhysicalCardLifecycleEntryPoint,
} from 'src/feature/PhysicalCardLifecycle/physicalCardLifecycle.types'

import type {
  CardActionsMenuProps,
  CardLifecycleMenuAction,
} from './CardActionsMenu'

/**
 * The two non-lifecycle actions, which have no controller of their own.
 *
 * Both arrive from the page: Adjust limit is a route change (a feature must not
 * read routing) and Add to Wallet needs the cardholder verdict, which comes from
 * the credit card's user vs. the signed-in user.
 */
export type PhysicalCardActionsMenuOptions = {
  /** Deep links to the Spend Limit tile. Omitted → the item still renders inert. */
  onAdjustLimit?: () => void
  /**
   * Whether the viewer may provision this card to a device. Cardholder-scoped
   * per the card-management design handoff §6 — Add to Wallet pushes live
   * credentials, so only the person holding the card sees it. Fails closed.
   */
  canAddToWallet?: boolean
  onAddToWallet?: () => void
}

export const physicalCardActionsMenuPropsFor = (
  lifecycleControls: PhysicalCardLifecycleControls,
  entryPoint: PhysicalCardLifecycleEntryPoint,
  options: PhysicalCardActionsMenuOptions = {},
): CardActionsMenuProps => {
  const busyActions: CardLifecycleMenuAction[] = []
  if (lifecycleControls.isActionInProgress('replace')) {
    busyActions.push('replaceCard')
  }
  if (lifecycleControls.isActionInProgress('lostOrStolen')) {
    busyActions.push('reportLostOrStolen')
  }
  if (lifecycleControls.isActionInProgress('reportFraud')) {
    busyActions.push('reportFraud')
  }
  if (lifecycleControls.isActionInProgress('cancel')) {
    busyActions.push('cancelCard')
  }

  return {
    capabilities: {
      addToWallet: options.canAddToWallet === true,
      replaceCard: lifecycleControls.availability.replace,
      reportLostOrStolen: lifecycleControls.availability.lostOrStolen,
      reportFraud: lifecycleControls.availability.reportFraud,
      cancelCard: lifecycleControls.availability.cancel,
    },
    busyActions,
    onAdjustLimit: options.onAdjustLimit,
    onAddToWallet: options.onAddToWallet,
    onReplaceCard: () => lifecycleControls.openAction('replace', entryPoint),
    onReportLostOrStolen: () =>
      lifecycleControls.openAction('lostOrStolen', entryPoint),
    onReportFraud: () =>
      lifecycleControls.openAction('reportFraud', entryPoint),
    onCancelCard: () => lifecycleControls.openAction('cancel', entryPoint),
  }
}
